import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { type DragEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"

import { FilaModulo, type Modulo } from "./FilaModulo"

/**
 * Qué módulos se ofrecen, en qué sección y en qué orden.
 *
 * ⚠ **Esto NO concede ni quita permisos.** Apagar un módulo lo saca del menú y nada
 * más: su ruta sigue viva y el endpoint sigue contestando a quien tenga el permiso.
 * Es deliberado y es la distinción que el 2.0 no hizo — allí el menú ERA el permiso,
 * con grano de módulo, y como la autorización era opcional por vista, veintinueve de
 * sus ochenta pantallas se alcanzaban escribiendo la ruta igual.
 *
 * ⚠ **El texto de arriba no es decorativo.** Sin él, quien apague un módulo aquí se
 * quedará creyendo que lo cerró, y esa creencia es peor que no tener la pantalla.
 *
 * ⚠ **Se arrastra para cambiar de sección, y el selector «Sección» hace lo mismo.**
 * Los dos, no uno: WCAG 2.5.7 exige una alternativa de un solo puntero para toda
 * operación de arrastre. El orden DENTRO de una sección sigue siendo cosa de las
 * flechas, y no del arrastre, porque son dos preguntas distintas —«¿dónde va?» y
 * «¿en qué puesto?»— y mezclarlas obliga a acertar el sitio exacto al soltar.
 *
 * ⚠ **Al cambiar de sección, el módulo se coloca AL FINAL.** Insertarlo en el punto
 * donde se soltó obligaría a renumerar el resto de la sección —una escritura por
 * fila— para que no chocaran dos `orden` iguales. Cae al final y se sube con las
 * flechas, que es una pulsación más y ninguna sorpresa.
 */

/**
 * Las secciones que el sistema conoce.
 *
 * ⚠ **Es una constante y no se deduce sólo de los datos**, y ésa es la razón: una
 * sección existe mientras algún módulo la use, así que sacar el último módulo de
 * «Finanzas» la haría desaparecer del selector — y ya no habría forma de devolver
 * nada a ella. Se unen con las que traigan los datos, por si alguien añade una
 * sección nueva desde la base.
 */
const SECCIONES_CONOCIDAS = [
  // ⚠ En el MISMO orden que `accesos.ORDEN_SECCIONES` del servidor, que es el que
  // manda en la barra lateral: de lo diario a la configuración.
  "Trabajo",
  // (28/10) Los departamentos de la estructura de la compañía (`accesos.DEPARTAMENTOS`).
  "Dirección Ejecutiva",
  "Operaciones Aduaneras",
  "Logística de Carga",
  "Comercial",
  "Finanzas",
  "Contabilidad",
  "Administración",
  "Imagen Corporativa",
  "Talento Humano",
  "Cumplimiento y Calidad",
  "Sistema",
] as const

/** Cuánto se deja entre dos módulos consecutivos al colocar uno al final. */
const SALTO_ORDEN = 10

export function EditorMenu() {
  const clienteQuery = useQueryClient()
  const [arrastrado, setArrastrado] = useState<number | null>(null)
  const [sobre, setSobre] = useState<string | null>(null)

  const modulos = useQuery({
    queryKey: ["modulos"],
    queryFn: () => api.get<Modulo[]>("/api/v1/modulos"),
  })

  const cambiar = useMutation({
    mutationFn: ({ id, ...datos }: { id: number } & Partial<Modulo>) =>
      api.patch(`/api/v1/modulos/${id}`, datos),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["modulos"] })
      // El menú de la barra lateral también: sin esto hay que recargar la página
      // para ver el cambio que uno acaba de hacer.
      clienteQuery.invalidateQueries({ queryKey: ["menu"] })
    },
  })

  if (modulos.isPending) {
    return (
      <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (modulos.isError || !modulos.data) {
    return (
      <p role="alert" className="py-12 text-center text-sm" style={{ color: "var(--destructive)" }}>
        {modulos.error instanceof ErrorApi ? modulos.error.message : "No se pudo cargar el menú"}
      </p>
    )
  }

  const datos = modulos.data
  const porSeccion = new Map<string, Modulo[]>()
  for (const m of datos) {
    const grupo = porSeccion.get(m.seccion)
    if (grupo === undefined) porSeccion.set(m.seccion, [m])
    else grupo.push(m)
  }

  // Las conocidas primero y en su orden; después, cualquiera que traigan los datos y
  // no esté en la lista. Así el selector no depende de que la sección tenga módulos.
  const conocidas: string[] = [...SECCIONES_CONOCIDAS]
  const secciones = [...conocidas, ...[...porSeccion.keys()].filter((s) => !conocidas.includes(s))]

  /** Intercambia el `orden` con el vecino. Dos PATCH, no una reordenación entera:
   *  mover una fila no debería reescribir las catorce. */
  const mover = (lista: Modulo[], indice: number, salto: number) => {
    const actual = lista[indice]
    const vecino = lista[indice + salto]
    if (actual === undefined || vecino === undefined) return
    cambiar.mutate({ id: actual.id, orden: vecino.orden })
    cambiar.mutate({ id: vecino.id, orden: actual.orden })
  }

  /** Lleva el módulo a otra sección, al final. Ver la cabecera del módulo. */
  const aSeccion = (modulo: Modulo, destino: string) => {
    if (destino === modulo.seccion) return
    const enDestino = porSeccion.get(destino) ?? []
    const ultimo = enDestino.reduce((mayor, m) => Math.max(mayor, m.orden), 0)
    cambiar.mutate({ id: modulo.id, seccion: destino, orden: ultimo + SALTO_ORDEN })
  }

  const soltarEn = (destino: string) => {
    setSobre(null)
    const modulo = datos.find((m) => m.id === arrastrado)
    setArrastrado(null)
    if (modulo) aSeccion(modulo, destino)
  }

  const permitirSoltar = (evento: DragEvent, destino: string) => {
    if (arrastrado === null) return
    // Sin `preventDefault` el navegador no considera la zona un destino válido y el
    // `drop` no llega nunca.
    evento.preventDefault()
    setSobre(destino)
  }

  return (
    <div>
      <p className="mb-4 superficie p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
        Apagar un módulo lo saca del menú de todo el mundo. <strong>No cierra su pantalla</strong>:
        quien tenga el permiso y la dirección sigue entrando. Quién puede qué se decide en «Roles y
        permisos».
      </p>

      <p className="mb-4 text-xs" style={{ color: "var(--fg-muted)" }}>
        Arrastra un módulo a otra sección, o cámbiala en su selector. Llega al final de la sección
        de destino; las flechas lo suben.
      </p>

      {cambiar.error && (
        <p
          role="alert"
          className="mb-3 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {cambiar.error instanceof ErrorApi ? cambiar.error.message : "No se pudo guardar"}
        </p>
      )}

      <div className="space-y-5">
        {secciones.map((seccion) => {
          const lista = porSeccion.get(seccion) ?? []
          const destacada = sobre === seccion && arrastrado !== null
          return (
            <section
              key={seccion}
              // ⚠ `aria-label` no es adorno: convierte la sección en una región con
              // nombre. Sin él es un elemento estático con manejadores de arrastre
              // —Biome lo marca—, y para un lector de pantalla la zona donde se
              // suelta no tiene nombre que anunciar.
              aria-label={`Sección ${seccion}`}
              data-testid={`seccion-grupo-${seccion}`}
              onDragOver={(e) => permitirSoltar(e, seccion)}
              onDragLeave={() => setSobre((s) => (s === seccion ? null : s))}
              onDrop={(e) => {
                e.preventDefault()
                soltarEn(seccion)
              }}
            >
              <h3 className="rotulo mb-2">{seccion}</h3>
              <ul
                className="superficie overflow-hidden"
                style={{
                  // El destino se marca con el borde de acento, no moviendo nada: una
                  // lista que se reacomoda mientras se arrastra hace perder el sitio.
                  outline: destacada ? "2px solid var(--primary-texto)" : undefined,
                  outlineOffset: "2px",
                  // Una sección vacía tiene que poder recibir algo: sin altura mínima
                  // no hay dónde soltar y la sección deja de existir en la práctica.
                  minHeight: lista.length === 0 ? "3rem" : undefined,
                }}
              >
                {lista.length === 0 && (
                  <li className="px-3 py-3 text-xs" style={{ color: "var(--fg-muted)" }}>
                    Sin módulos. Arrastra uno aquí.
                  </li>
                )}
                {lista.map((m, i) => (
                  <FilaModulo
                    key={m.id}
                    modulo={m}
                    primera={i === 0}
                    ultima={i === lista.length - 1}
                    secciones={secciones}
                    arrastrada={arrastrado === m.id}
                    onSubir={() => mover(lista, i, -1)}
                    onBajar={() => mover(lista, i, 1)}
                    onSeccion={(destino) => aSeccion(m, destino)}
                    onAlternar={() => cambiar.mutate({ id: m.id, activo: !m.activo })}
                    onArrastrar={(evento) => {
                      evento.dataTransfer.setData("text/plain", String(m.id))
                      evento.dataTransfer.effectAllowed = "move"
                      setArrastrado(m.id)
                    }}
                    onSoltarArrastre={() => {
                      setArrastrado(null)
                      setSobre(null)
                    }}
                  />
                ))}
              </ul>
            </section>
          )
        })}
      </div>
    </div>
  )
}
